/**
 * ThemeContext - React context for theme management
 * LAYER-SHARED-INTERACTIONS
 *
 * Provides theme state and controls to all components via context.
 * Use useThemeContext() hook to access theme in components.
 */
import React, { createContext, useContext, useEffect } from 'react';
import { useTheme, type Theme, type ResolvedTheme, type UseThemeReturn } from '../hooks';

// Context with default values (will be overwritten by provider)
const ThemeContext = createContext<UseThemeReturn | undefined>(undefined);

export interface ThemeProviderProps {
  children: React.ReactNode;
  /** Default theme to use if no preference is stored */
  defaultTheme?: Theme;
  /** Force a specific theme (disables user preference) */
  forcedTheme?: Theme;
}

/**
 * ThemeProvider wraps the application to provide theme context.
 *
 * @example
 * // In app root
 * <ThemeProvider>
 *   <App />
 * </ThemeProvider>
 *
 * // With default theme
 * <ThemeProvider defaultTheme="dark">
 *   <App />
 * </ThemeProvider>
 */
export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  defaultTheme,
  forcedTheme,
}) => {
  const themeState = useTheme();

  // Apply default theme on mount if no stored preference
  useEffect(() => {
    if (defaultTheme && !localStorage.getItem('esig-theme')) {
      themeState.setTheme(defaultTheme);
    }
  }, [defaultTheme, themeState]);

  // Handle forced theme
  useEffect(() => {
    if (forcedTheme) {
      themeState.setTheme(forcedTheme);
    }
  }, [forcedTheme, themeState]);

  return (
    <ThemeContext.Provider value={themeState}>
      {children}
    </ThemeContext.Provider>
  );
};

/**
 * Hook to access theme context
 *
 * @example
 * const { theme, setTheme, isDark, toggleTheme } = useThemeContext();
 */
export function useThemeContext(): UseThemeReturn {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useThemeContext must be used within a ThemeProvider');
  }
  return context;
}

/**
 * Component to render content based on current theme
 *
 * @example
 * <ThemeSwitch
 *   light={<SunIcon />}
 *   dark={<MoonIcon />}
 * />
 */
export const ThemeSwitch: React.FC<{
  light: React.ReactNode;
  dark: React.ReactNode;
}> = ({ light, dark }) => {
  const { isDark } = useThemeContext();
  return <>{isDark ? dark : light}</>;
};

export { Theme, ResolvedTheme };
export default ThemeProvider;
